import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import logger from '../utility/logger';

export const STALL_WATCHDOG_POLL_MS = 1000;

/**
 * Largest currentTime movement still treated as "no progress".
 *
 * A micro-seek is a write to currentTime, and the element may land a little off the requested value
 * as it snaps to a frame boundary. Without this tolerance the watchdog reads its own nudge as
 * recovery, clears the attempt counter, and keeps nudging a permanently wedged stream forever
 * instead of surrendering to the error path. Real playback advances roughly a second per poll, so
 * it clears this bar comfortably.
 */
const MAX_PROGRESS_EPSILON = 0.05;

/** Which of the two freeze shapes triggered a nudge. */
export type StallKind = 'playhead' | 'frames';

export interface StallNudgeDetails {
    /**
     * `playhead` — currentTime stopped advancing while the element claimed to be playing.
     * `frames` — currentTime kept advancing but the decoder stopped producing video frames, which
     * is the "video froze, audio carried on" report.
     */
    kind: StallKind;
    /** Position the element was wedged at, in seconds. */
    position: number;
    /** How long the freeze had lasted before the nudge, in milliseconds. */
    stalledForMs: number;
    /** 1-based index of this nudge within the current freeze. */
    attempt: number;
    readyState: number;
    bufferedAhead: number;
    /** Total decoded video frames at the moment of the nudge, where the browser reports it. */
    totalVideoFrames: number | null;
}

export interface UseStallWatchdogProps {
    videoElementRef: MutableRefObject<HTMLVideoElement | undefined>;
    enabled: boolean;
    isBuffering?: boolean;
    thresholdMs: number;
    maxNudges: number;
    nudgeSeconds: number;
    /** How long the frame counter may stay flat while the playhead advances. */
    frameFreezeThresholdMs: number;
    frameFreezeMaxNudges: number;
    onNudge?: (details: StallNudgeDetails) => void;
}

const getBufferedAhead = (video: HTMLVideoElement): number => {
    const { buffered, currentTime } = video;

    for (let index = 0; index < buffered.length; index += 1) {
        if (currentTime >= buffered.start(index) && currentTime <= buffered.end(index)) {
            return buffered.end(index) - currentTime;
        }
    }

    return 0;
};

const HAVE_FUTURE_DATA = 3;

const canRecoverPlayheadStall = (
    readyState: number,
    bufferedAhead: number,
    isBuffering: boolean,
    nudgeSeconds: number,
): boolean => !isBuffering
    && readyState >= HAVE_FUTURE_DATA
    && bufferedAhead >= nudgeSeconds;

interface VideoPlaybackQualityLike {
    totalVideoFrames?: number;
}

type VideoWithFrameCounters = HTMLVideoElement & {
    getVideoPlaybackQuality?: () => VideoPlaybackQualityLike;
    webkitDecodedFrameCount?: number;
};

/**
 * Decoded frame count, or null where the browser exposes no counter at all.
 */
export const readTotalVideoFrames = (video: HTMLVideoElement): number | null => {
    const source = video as VideoWithFrameCounters;

    const quality = source.getVideoPlaybackQuality?.();

    if (quality && typeof quality.totalVideoFrames === 'number') {
        return quality.totalVideoFrames;
    }

    if (typeof source.webkitDecodedFrameCount === 'number') {
        return source.webkitDecodedFrameCount;
    }

    return null;
};

/**
 * Recovers two distinct freezes that the player itself does not report.
 *
 * **Playhead freeze** — the element claims to be playing, fires no `waiting` event, and simply
 * stops advancing currentTime. This polls currentTime rather than listening for `waiting`, because
 * in the failure mode it targets the element never emits `waiting` at all. The threshold sits well
 * above hls.js's own gap-nudging (1250 ms detection plus a 2 s watchdog period, three retries) so
 * the two never fight over the same stall — hls.js gets first refusal.
 *
 * **Frame freeze** — currentTime advances normally and audio keeps playing, but the decoder stops
 * producing video frames. Complaint analysis of the January–May 2026 cases showed viewers reporting
 * exactly this ("the video froze but I could still hear the audio"), and a playhead-based watchdog
 * is blind to it by construction. Detection compares the decoded frame counter against a playhead
 * that is demonstrably moving; recovery is the same micro-seek, which forces the decoder to flush
 * and re-render.
 *
 * Frame detection engages only once the element has been seen rendering video, so audio-only
 * programmes and browsers without a frame counter never trigger it, and it pauses while the tab is
 * hidden, where browsers legitimately stop painting frames.
 */
export const useStallWatchdog = ({
    videoElementRef,
    enabled,
    isBuffering = false,
    thresholdMs,
    maxNudges,
    nudgeSeconds,
    frameFreezeThresholdMs,
    frameFreezeMaxNudges,
    onNudge,
}: UseStallWatchdogProps) => {
    const lastPositionRef = useRef<number | null>(null);
    const lastAdvanceAtRef = useRef<number>(Date.now());
    const nudgeCountRef = useRef(0);

    const lastFrameCountRef = useRef<number | null>(null);
    const lastFrameAdvanceAtRef = useRef<number>(Date.now());
    const frameNudgeCountRef = useRef(0);
    const hasRenderedVideoRef = useRef(false);

    const onNudgeRef = useRef(onNudge);

    useEffect(() => {
        onNudgeRef.current = onNudge;
    }, [onNudge]);

    const reset = useCallback(() => {
        lastPositionRef.current = null;
        lastAdvanceAtRef.current = Date.now();
        nudgeCountRef.current = 0;
        lastFrameCountRef.current = null;
        lastFrameAdvanceAtRef.current = Date.now();
        frameNudgeCountRef.current = 0;
        hasRenderedVideoRef.current = false;
    }, []);

    useEffect(() => {
        if (!enabled) {
            reset();
            return;
        }

        const progressEpsilon = Math.min(MAX_PROGRESS_EPSILON, nudgeSeconds / 2);

        const applyNudge = (
            video: HTMLVideoElement,
            details: StallNudgeDetails,
        ): boolean => {
            logger.log('Stall watchdog applying micro-seek', details);

            const target = details.position + nudgeSeconds;

            try {
                video.currentTime = target;
            } catch (error) {
                logger.error('Stall watchdog micro-seek failed', { error, details });
                return false;
            }

            // Treat the value just written as the new baseline, so the next poll compares against
            // it rather than reading the nudge itself as playback resuming.
            lastPositionRef.current = target;

            onNudgeRef.current?.(details);
            return true;
        };

        const intervalId = window.setInterval(() => {
            const video = videoElementRef.current;
            const now = Date.now();

            if (!video || video.paused || video.ended || video.seeking) {
                lastPositionRef.current = null;
                lastAdvanceAtRef.current = now;
                lastFrameCountRef.current = null;
                lastFrameAdvanceAtRef.current = now;
                return;
            }

            const position = video.currentTime;
            const hasMoved = lastPositionRef.current === null
                || Math.abs(position - lastPositionRef.current) > progressEpsilon;

            if (!hasMoved) {
                // The playhead itself is wedged. Frame counters are meaningless here — of course
                // no frames are being decoded — so the frame detector stands down until it moves.
                lastFrameCountRef.current = null;
                lastFrameAdvanceAtRef.current = now;

                const stalledForMs = now - lastAdvanceAtRef.current;

                if (stalledForMs < thresholdMs) return;
                if (nudgeCountRef.current >= maxNudges) return;

                const readyState = video.readyState;
                const bufferedAhead = getBufferedAhead(video);

                if (!canRecoverPlayheadStall(readyState, bufferedAhead, isBuffering, nudgeSeconds)) {
                    return;
                }

                nudgeCountRef.current += 1;

                const applied = applyNudge(video, {
                    kind: 'playhead',
                    position,
                    stalledForMs,
                    attempt: nudgeCountRef.current,
                    readyState,
                    bufferedAhead,
                    totalVideoFrames: readTotalVideoFrames(video),
                });

                // Give the nudge the full threshold to take effect before considering another one.
                if (applied) lastAdvanceAtRef.current = now;
                return;
            }

            lastPositionRef.current = position;
            lastAdvanceAtRef.current = now;
            nudgeCountRef.current = 0;

            // ---- The playhead is moving. Is the picture moving with it? ----

            // Browsers stop painting frames for a hidden tab as a matter of policy, and audio-only
            // programmes have no video track to count.
            if (document.visibilityState === 'hidden' || !video.videoWidth) {
                lastFrameCountRef.current = null;
                lastFrameAdvanceAtRef.current = now;
                return;
            }

            const frames = readTotalVideoFrames(video);

            if (frames === null) {
                // No frame counter on this browser; the playhead detector is all we have.
                return;
            }

            if (lastFrameCountRef.current === null || frames > lastFrameCountRef.current) {
                if (frames > 0) hasRenderedVideoRef.current = true;
                lastFrameCountRef.current = frames;
                lastFrameAdvanceAtRef.current = now;
                frameNudgeCountRef.current = 0;
                return;
            }

            // Never act until video has demonstrably rendered at least once, so a slow start is not
            // mistaken for a decoder that has given up.
            if (!hasRenderedVideoRef.current) return;

            const frozenForMs = now - lastFrameAdvanceAtRef.current;

            if (frozenForMs < frameFreezeThresholdMs) return;
            if (frameNudgeCountRef.current >= frameFreezeMaxNudges) return;

            frameNudgeCountRef.current += 1;

            const applied = applyNudge(video, {
                kind: 'frames',
                position,
                stalledForMs: frozenForMs,
                attempt: frameNudgeCountRef.current,
                readyState: video.readyState,
                bufferedAhead: getBufferedAhead(video),
                totalVideoFrames: frames,
            });

            if (applied) lastFrameAdvanceAtRef.current = now;
        }, STALL_WATCHDOG_POLL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [
        enabled,
        frameFreezeMaxNudges,
        frameFreezeThresholdMs,
        isBuffering,
        maxNudges,
        nudgeSeconds,
        reset,
        thresholdMs,
        videoElementRef,
    ]);

    return { resetStallWatchdog: reset };
};

export default useStallWatchdog;
